/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Customer testimonials section with carousel on mobile
 */
import React, { useRef, useState } from 'react';

import { StarIcon } from './svg';

interface Testimonial {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: string;
}

const TestimonialsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const testimonials: Testimonial[] = [
    {
      id: 'testimonial-1',
      rating: 5,
      title: 'Faster Than Any Translator I’ve Used',
      content:
        'FalcoCut completely transformed my workflow. I can translate and dub my videos in minutes, which helps me connect with viewers across regions without hiring translators.',
      author: '- Lucas Martinez, YouTuber',
    },
    {
      id: 'testimonial-2',
      rating: 5,
      title: 'Essential for Training International Teams',
      content:
        'As a trainer working with international teams, accurate subtitles and natural-sounding dubbing are essential. FalcoCut makes my training sessions accessible and professional.',
      author: '- Elena Rodriguez, Corporate Trainer',
    },
    {
      id: 'testimonial-3',
      rating: 5,
      title: 'The Best Tool for Global Marketing',
      content:
        'We localized our product demos faster than ever before. The AI-generated subtitles and voiceovers feel natural and polished—far better than costly traditional translation.',
      author: '-David Kim, Marketing Manager',
    },
    {
      id: 'testimonial-4',
      rating: 5,
      title: 'Boosted Our E-Commerce Sales',
      content:
        'We translated our product videos into multiple languages for international markets. With FalcoCut, our ads and demos feel natural to every audience, and sales in new regions grew significantly.',
      author: '- Sophie Chen, E-Commerce Store Owner',
    },
    {
      id: 'testimonial-5',
      rating: 5,
      title: 'Perfect for Online Learning',
      content:
        'My online classes now reach learners worldwide. FalcoCut’s voice cloning makes me sound authentic in every language, and the subtitles are highly accurate.',
      author: '- Aya Tanaka, Educator',
    },
    {
      id: 'testimonial-6',
      rating: 5,
      title: 'Made My Podcast Multilingual',
      content:
        'I never imagined I could turn my podcast into a multilingual show so easily. FalcoCut gave me realistic dubbing and subtitles that truly expanded my audience.',
      author: '-Carlos Jimenez, Podcaster',
    },
  ];

  // 处理移动端滚动
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / testimonials.length;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex !== activeIndex) {
        setActiveIndex(currentIndex);
      }
    }
  };

  // 处理指示器点击
  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / testimonials.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  // 渲染单个评价卡片
  const renderTestimonialCard = (testimonial: Testimonial) => (
    <div className="bg-white rounded-md p-6 pad:p-8 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
      {/* 星星评分 */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: testimonial.rating }).map((_, index) => (
          <StarIcon key={index} className="w-5 h-5 text-[#FF8A3D]" />
        ))}
      </div>

      {/* 标题 */}
      <h3 className="text-lg pad:text-xl font-bold text-[#0F1117] mb-3">
        {testimonial.title}
      </h3>

      {/* 评价内容 */}
      <p className="text-sm leading-relaxed mb-6 flex-1">{testimonial.content}</p>

      {/* 作者信息 */}
      <div className="flex items-centerpt-4 text-[#333333] text-sm">
        {testimonial.author}
      </div>
    </div>
  );

  return (
    <section className="w-full bg-[#F5F7FA] py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-10 pad:mb-12 sm:mb-16">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] mb-3 pad:mb-4">
            What Our Customers Say
          </h2>
        </div>

        {/* PC 端：3 列网格布局 */}
        <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id}>{renderTestimonialCard(testimonial)}</div>
          ))}
        </div>

        {/* 移动端：轮播 */}
        <div className="sm:hidden">
          {/* 轮播容器 */}
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="w-full flex-shrink-0 snap-center px-2">
                {renderTestimonialCard(testimonial)}
              </div>
            ))}
          </div>

          {/* 指示器 */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleIndicatorClick(index)}
                className={`
                  h-2 rounded-full transition-all duration-300
                  ${activeIndex === index ? 'w-8 bg-[#3366FF]' : 'w-2 bg-gray-300'}
                `}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
